// src/lib/templateUtils.ts - Emergency fallback to prevent 406 errors
import { CategoryOption, CategoryTemplate, FinancialData } from '../../types';
import { templateApi, authApi, projectApi } from '../api/authApi';
import { ensureDefaultTemplateExists } from '../categories/defaultTemplate';

// Emergency fallback function for template loading
const emergencyFallback = async (): Promise<CategoryTemplate | null> => {
  console.warn('Using emergency fallback for template loading due to auth issues');
  
  // Return a basic default template to prevent app crashes
  const defaultTemplate: CategoryTemplate = {
    id: 'emergency-default',
    name: 'Emergency Default Template', 
    user_id: 'emergency-user',
    categories: [
      {
        value: 'N/A',
        label: 'N/A',
        type: 'n/a',
        fsType: 'balance-sheet',
        tagsInclude: [],
        tagsExclude: [],
        isNonCash: false,
        isPpe: false,
        isDebt: false,
        isEquityCapital: false
      }
    ],
    is_default: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  return defaultTemplate;
};

// Helper for user-scoped localStorage keys
const ACTIVE_TEMPLATE_LS_KEY = (uid: string) => `activeTemplateId:${uid}`;

// Database operations now handled through secure API

export const loadActiveTemplate = async (): Promise<CategoryTemplate | null> => {
  try {
    // Validate user session first
    const sessionValidation = await authApi.validateSession();
    if (!sessionValidation.valid) {
      console.error('Invalid session for template loading');
      return null;
    }
    
    const activeTemplateId = localStorage.getItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id));
    
    let templateData;
    if (activeTemplateId) {
      try {
        // Get specific template by ID
        templateData = await templateApi.getById(activeTemplateId);
      } catch (error: any) {
        const status = (error?.response?.status ?? error?.status) as number | undefined;
        if (status === 404) {
          console.warn('🧹 Clearing invalid template ID from localStorage:', activeTemplateId);
          // Clear invalid template ID from localStorage
          localStorage.removeItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id));
          // Fall back to default template
          const templates = await templateApi.getAll();
          templateData = templates.find((t: any) => t.is_default) || templates[0];
          // Persist the recovered default so we don't repeat getAll next time
          if (templateData?.id) {
            localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id), templateData.id);
          }
        } else {
          // Network/auth/server error — surface to outer catch instead of mutating client state
          throw error;
        }
      }
    } else {
      // Get all templates and find default
      const templates = await templateApi.getAll();
      templateData = templates.find(t => t.is_default) || templates[0];
    }
    
    if (!templateData) return null;

    // Transform the API data into our CategoryTemplate type
    const template: CategoryTemplate = {
      id: templateData.id,
      name: templateData.name,
      user_id: templateData.user_id,
      categories: templateData.categories as CategoryOption[],
      is_default: templateData.is_default,
      created_at: templateData.created_at,
      updated_at: templateData.updated_at
    };

    return template;
  } catch (error: any) {
    console.error('Error loading active template:', error);
    return null;
  }
};

export const saveActiveTemplateId = (templateId: string, userId: string) => {
  localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(userId), templateId);
};

export const normalizeFinancialData = (
  financialData: FinancialData,
  template: CategoryTemplate
): FinancialData => {
  const validCategories = new Set(template.categories.map(c => c.value));
  
  const newMappings = { ...financialData.mappings };
  
  const newAccounts = financialData.accounts.map(account => {
    const currentCategory = financialData.mappings[account.accountNumber]?.category || account.category;
    
    if (!validCategories.has(currentCategory)) {
      const newCategory = 'N/A';
      
      newMappings[account.accountNumber] = {
        accountNumber: account.accountNumber,
        name: account.name,
        category: newCategory
      };
      
      return {
        ...account,
        category: newCategory
      };
    }
    
    return account;
  });

  return {
    accounts: newAccounts,
    mappings: newMappings
  };
};

export const updateProjectWithNormalizedData = async (
  projectId: string,
  financialData: FinancialData,
  isAlreadyMerged: boolean = false,
  periodType?: 'monthly' | 'quarterly' | 'annual'
): Promise<boolean> => {
  try {
    let dataToSave = financialData;
    
    // If the data is already merged (from FileUpload conflict resolution), save it directly
    if (isAlreadyMerged) {
      console.log('[templateUtils] Data is already merged - saving directly to backend', {
        accountCount: financialData.accounts?.length || 0,
        mappingsCount: Object.keys(financialData.mappings || {}).length
      });
      dataToSave = financialData;
    }
    // If project has existing financial data, we need to be careful about merging
    else {
      // CRITICAL FIX: First fetch existing project data to preserve it
      const existingProject = await projectApi.getById(projectId);
        
      if (!existingProject) {
        console.error('Error fetching existing project data');
        throw new Error('Project not found');
      }
      
      if (existingProject?.financial_data) {
        const existingData = existingProject.financial_data as FinancialData;
        
        // Check if the incoming data contains new account-month combinations (additional data scenario)
        const existingAccountKeys = new Set(existingData.accounts?.map(acc => `${acc.accountNumber}-${acc.month}`) || []);
        
        const hasNewData = financialData.accounts?.some(acc => {
          const key = `${acc.accountNumber}-${acc.month}`;
          return !existingAccountKeys.has(key);
        });
        
        // If we're adding new data (not a complete replacement), merge properly
        if (hasNewData && existingData.accounts?.length > 0) {
          console.log('[templateUtils] Detected additional data scenario - merging with existing data');
          
          // Create merged data preserving all existing entries
          const mergedAccounts = [...existingData.accounts];
          const existingLookup = new Map(existingData.accounts.map(acc => [`${acc.accountNumber}-${acc.month}`, acc]));
          
          // Add new accounts that don't conflict, update existing ones if needed
          financialData.accounts?.forEach(newAcc => {
            const key = `${newAcc.accountNumber}-${newAcc.month}`;
            const existingAcc = existingLookup.get(key);
            
            if (!existingAcc) {
              // New account entry - add it
              mergedAccounts.push(newAcc);
            } else {
              // Existing account - update it but preserve categories
              const updatedAcc = {
                ...newAcc,
                category: existingAcc.category || newAcc.category, // Preserve existing category
                type: existingAcc.type || newAcc.type // Preserve existing type
              };
              
              // Replace the existing account with updated version
              const index = mergedAccounts.findIndex(acc => `${acc.accountNumber}-${acc.month}` === key);
              if (index >= 0) {
                mergedAccounts[index] = updatedAcc;
              }
            }
          });
          
          // Merge mappings as well
          const mergedMappings = {
            ...existingData.mappings,
            ...financialData.mappings
          };
          
          dataToSave = {
            accounts: mergedAccounts,
            mappings: mergedMappings
          };
          
          console.log('[templateUtils] Merged data:', {
            existingAccountsCount: existingData.accounts?.length || 0,
            incomingAccountsCount: financialData.accounts?.length || 0,
            mergedAccountsCount: mergedAccounts.length
          });
        } else {
          // Complete replacement scenario
          dataToSave = financialData;
        }
      }
    }
    
    console.log('[templateUtils] About to save via API:', {
      projectId,
      isAlreadyMerged,
      dataToSaveAccountCount: dataToSave.accounts?.length || 0,
      dataToSaveMappingsCount: Object.keys(dataToSave.mappings || {}).length,
      sampleAccounts: (dataToSave.accounts || []).slice(0, 5).map(acc => ({
        accountNumber: acc.accountNumber,
        name: acc.name,
        month: acc.month,
        debit: acc.debit,
        credit: acc.credit
      }))
    });

    // CRITICAL FIX: Actually save to the database via secure API!
    const updateData: any = {
      financial_data: dataToSave,
      updated_at: new Date().toISOString()
    };
    
    // Add period type if detected
    if (periodType) {
      updateData.period_type = periodType;
      console.log('[templateUtils] Saving project with detected period type:', periodType);
    }
    
    await projectApi.update(projectId, updateData);
    
    console.log('[templateUtils] Successfully updated project with data:', {
      finalAccountCount: dataToSave.accounts?.length || 0,
      isAlreadyMerged
    });
    return true;
  } catch (error) {
    console.error('Error updating project with normalized data:', error);
    return false;
  }
};

// Load the active template when the app initializes
export const initializeActiveTemplate = async (): Promise<CategoryTemplate | null> => {
  try {
    // Validate user session first
    const sessionValidation = await authApi.validateSession();
    if (!sessionValidation.valid) {
      console.error('Invalid session for template initialization');
      return null;
    }
    
    // Ensure the user has a default template before proceeding
    await ensureDefaultTemplateExists(sessionValidation.user.id);
    
    // Try to get the active template ID from localStorage
    const activeTemplateId = localStorage.getItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id));
    
    let templateData;
    try {
      if (activeTemplateId) {
        try {
          // If we have an active template ID, load that template
          templateData = await templateApi.getById(activeTemplateId);
        } catch (error: any) {
          const status = (error?.response?.status ?? error?.status) as number | undefined;
          if (status === 404) {
            console.warn('🧹 Clearing invalid template ID from localStorage:', activeTemplateId);
            // Clear the invalid template ID from localStorage
            localStorage.removeItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id));
            
            // Fall back to default template
            const templates = await templateApi.getAll();
            templateData = templates.find(t => t.is_default);
          } else {
            // Network/auth/server error — surface to outer catch
            throw error;
          }
        }
      } else {
        // Otherwise load the default template
        const templates = await templateApi.getAll();
        templateData = templates.find(t => t.is_default);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      return null;
    }
    
    // Update localStorage with the loaded template
    if (templateData) {
      localStorage.setItem(ACTIVE_TEMPLATE_LS_KEY(sessionValidation.user.id), templateData.id);
      return templateData;
    }
    
    return null;
  } catch (error) {
    console.error('Error initializing active template:', error);
    return null;
  }
};

// If a project has no template_id, assign it the active template
export const ensureProjectHasTemplate = async (
  projectId: string, 
  activeTemplateId: string
): Promise<void> => {
  try {
    // Check if project has a template
    const project = await projectApi.getById(projectId);
      
    if (!project) {
      console.error('Error checking project template: project not found');
      return;
    }
    
    // If project has no template, assign the active one
    if (!project.template_id) {
      await projectApi.update(projectId, {
        template_id: activeTemplateId,
        updated_at: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error in ensureProjectHasTemplate:', error);
  }
};
